import "./customer-settings.css";

import { Switch } from "@tql/ui-components-react/kendo/inputs";
import { Label } from "@tql/ui-components-react/kendo/labels";
import { useRef, useState } from "react";

import type { CustomerTenderSettings } from "~/shared/schemas/auto-dispatch-schema.ts";

import { useAutoDispatchNavigation } from "../../hooks/use-auto-dispatch-navigation.ts";
import type { CustomerTenderSettingChange } from "../../hooks/use-update-customer-tender-settings.ts";
import type { TenderUserTypeId } from "../../utils/constants.ts";
import { tenderUserTypeIds } from "../../utils/constants.ts";

type TenderSettingSwitchProps = {
	checked: boolean;
	disabled: boolean;
	id: string;
	tenderUserTypeId: TenderUserTypeId;
	testId: string;
	onChange: (change: CustomerTenderSettingChange) => Promise<void>;
};

const TenderSettingSwitch = ({
	checked,
	disabled,
	id,
	tenderUserTypeId,
	testId,
	onChange,
}: TenderSettingSwitchProps) => {
	const [isPending, setIsPending] = useState(false);
	const isPendingRef = useRef(false);

	return (
		<Switch
			checked={checked}
			data-testid={testId}
			disabled={disabled || isPending}
			id={id}
			onChange={(event) => {
				if (isPendingRef.current) return;

				isPendingRef.current = true;
				setIsPending(true);
				void Promise.resolve(
					onChange({ tenderUserTypeId, enabled: event.value }),
				)
					.catch(() => undefined)
					.finally(() => {
						isPendingRef.current = false;
						setIsPending(false);
					});
			}}
		/>
	);
};

export const CustomerSettings = ({
	customerTenderSettings,
	isDisabled,
	onHandleChange,
}: {
	customerTenderSettings: CustomerTenderSettings | undefined;
	isDisabled: boolean;
	onHandleChange: (change: CustomerTenderSettingChange) => Promise<void>;
}) => {
	const { selectedCustomer } = useAutoDispatchNavigation();

	return (
		<div className={"customer-settings-container"}>
			<div className={"customer-settings-header"}>
				<div className={"customer-settings-header-cell"}>
					<Label editorId={"customer-tender-auto-dispatch"}>
						Customer Tender
					</Label>
				</div>
				<div className={"customer-settings-header-cell"}>
					<Label editorId={"internal-tender-auto-dispatch"}>
						Internal Tender
					</Label>
				</div>
				<div className={"customer-settings-header-cell"}>
					<Label editorId={"vendor-tender-auto-dispatch"}>
						Third Party Tender
					</Label>
				</div>
			</div>
			<div className={"customer-settings-row"}>
				<div className={"customer-settings-cell"}>
					<TenderSettingSwitch
						checked={
							customerTenderSettings?.canDispatchOnCustomerTender ?? false
						}
						disabled={!selectedCustomer || isDisabled}
						id={"customer-tender-auto-dispatch"}
						onChange={onHandleChange}
						tenderUserTypeId={tenderUserTypeIds.customer}
						testId={"customer-tender-auto-dispatch-switch"}
					/>
					Auto Dispatch{" "}
					{customerTenderSettings?.canDispatchOnCustomerTender ? "On" : "Off"}
				</div>
				<div className={"customer-settings-cell"}>
					<TenderSettingSwitch
						checked={customerTenderSettings?.canDispatchOnTQLTender ?? false}
						disabled={!selectedCustomer || isDisabled}
						id={"internal-tender-auto-dispatch"}
						onChange={onHandleChange}
						tenderUserTypeId={tenderUserTypeIds.internal}
						testId={"internal-tender-auto-dispatch-switch"}
					/>
					Auto Dispatch{" "}
					{customerTenderSettings?.canDispatchOnTQLTender ? "On" : "Off"}
				</div>
				<div className={"customer-settings-cell"}>
					<TenderSettingSwitch
						checked={customerTenderSettings?.canDispatchOnVendorTender ?? false}
						disabled={!selectedCustomer || isDisabled}
						id={"vendor-tender-auto-dispatch"}
						onChange={onHandleChange}
						tenderUserTypeId={tenderUserTypeIds.vendor}
						testId={"vendor-tender-auto-dispatch-switch"}
					/>
					Auto Dispatch{" "}
					{customerTenderSettings?.canDispatchOnVendorTender ? "On" : "Off"}
				</div>
			</div>
		</div>
	);
};
